import { LEAD_TYPE_LABEL, LeadType } from "views/leads-id/types";

const LEAD_TYPE_DISPLAY_ORDER: LeadType[] = [
  LeadType.Tenant,
  LeadType.Owner,
  LeadType.Realtor
];

const DEFAULT_LEAD_TYPES: LeadType[] = [LeadType.Tenant, LeadType.Realtor];

// Before realtors, Tenant + Owner was every type, so the old filter stored
// "Any" as that pair in either order (cleared, or ticked one after the other).
// A Tenant + Owner choice made now carries this marker so it isn't read as Any.
export const EXPLICIT_LEAD_TYPES_MARKER = "explicit";

const isLeadType = (value: string): value is LeadType =>
  LEAD_TYPE_DISPLAY_ORDER.some((leadType) => leadType === value);

const isTenantOwnerPair = (leadTypes: string[]): boolean =>
  leadTypes.length === 2 &&
  leadTypes.includes(LeadType.Tenant) &&
  leadTypes.includes(LeadType.Owner);

const isLegacyAnySelection = (leadTypes: string[]): boolean =>
  isTenantOwnerPair(leadTypes);

export const toStoredLeadTypes = (leadTypes: LeadType[]): string[] => {
  const ordered = LEAD_TYPE_DISPLAY_ORDER.filter((leadType) =>
    leadTypes.includes(leadType)
  );
  return isTenantOwnerPair(ordered)
    ? [...ordered, EXPLICIT_LEAD_TYPES_MARKER]
    : ordered;
};

export const getAvailableLeadTypes = ({
  isOwnerLeads,
  isShowings
}: {
  isOwnerLeads: boolean;
  isShowings: boolean;
}): LeadType[] =>
  LEAD_TYPE_DISPLAY_ORDER.filter(
    (leadType) =>
      leadType === LeadType.Tenant ||
      (leadType === LeadType.Owner && isOwnerLeads) ||
      (leadType === LeadType.Realtor && isShowings)
  );

export const getSelectedLeadTypes = (
  leadTypes: string[] | undefined,
  available: LeadType[],
  applyDefault: boolean
): LeadType[] => {
  if (available.length < 2) return [];
  if (leadTypes === undefined)
    return applyDefault
      ? DEFAULT_LEAD_TYPES.filter((leadType) => available.includes(leadType))
      : [];
  if (isLegacyAnySelection(leadTypes)) return [];
  return Array.from(
    new Set(
      leadTypes.filter(
        (value): value is LeadType =>
          isLeadType(value) && available.includes(value)
      )
    )
  );
};

export const isAnyLeadTypeSelection = (
  selected: LeadType[],
  available: LeadType[]
): boolean =>
  selected.length === 0 ||
  available.every((leadType) => selected.includes(leadType));

export const isOnlyOwnerSelected = (selected: LeadType[]): boolean =>
  selected.length === 1 && selected[0] === LeadType.Owner;

export const mixesPipelines = (
  selected: LeadType[],
  available: LeadType[]
): boolean => {
  const effective = selected.length === 0 ? available : selected;
  return (
    effective.includes(LeadType.Owner) &&
    effective.some((leadType) => leadType !== LeadType.Owner)
  );
};

export const getLeadTypesLabel = (
  selected: LeadType[],
  available: LeadType[]
): string => {
  if (isAnyLeadTypeSelection(selected, available)) return "Any";
  return LEAD_TYPE_DISPLAY_ORDER.filter((leadType) =>
    selected.includes(leadType)
  )
    .map((leadType) => LEAD_TYPE_LABEL[leadType])
    .join(", ");
};

export const getLeadTypesParam = (
  selected: LeadType[],
  available: LeadType[]
): LeadType[] | undefined => {
  if (isAnyLeadTypeSelection(selected, available)) return undefined;
  return selected;
};
